import React, { useState, useEffect, useRef, useCallback, useId } from "react";
import DataTable from "react-data-table-component";
import {
  getFeeCaps,
  getTerminals,
  getSingleTerminal,
  addOrUpdateTerminal,
  clearTerminalLoginAttempts,
  createEnrolmentCode,
} from "../../api/terminalApi";
import Select from "react-select";
import Loader from "../common/Loader";
import LabeledToggle from "../common/LabeledToggle";
import ActivityLogModal, {
  TERMINAL_FILTER_TYPES,
} from "../common/ActivityLogModal";
import PermissionGate from "../common/PermissionGate";
import { PERMISSIONS } from "../../api/permissions";
import { usePermissions } from "../../context/PermissionContext";

const SESSION_KEY_PREFIX = "enrolmentCode_";
const EXPIRY_KEY_PREFIX = "enrolmentExpiry_";
const ENROLMENT_DURATION_MS = 10 * 60 * 1000; // 10 minutes

// ---- Fee field caps (fallback defaults; live values come from getFeeCaps API) ----
const DEFAULT_P_FEE_CAP = 100; // default percentage fee cap
const DEFAULT_D_FEE_CAP = 99999.99; // default fixed/dollar fee cap
const MAX_PURCHASE = 29999.99;
const MAX_FEE = 99999.99;

// ---- Number helpers (keep strings in state, parse only for validation/submit) ----
const toNum = (v, fallback = 0) => {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// ---- Numeric input sanitiser (non-negative decimals only, capped) ----
const sanitizeDecimal = (raw, max) => {
  if (raw === "" || raw === null || raw === undefined) return "";

  let s = String(raw).replace(/[^0-9.]/g, "");

  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }

  if (firstDot !== -1) {
    const [intPart, decPart] = s.split(".");
    s = `${intPart}.${(decPart || "").slice(0, 2)}`;
  } else if (s.length > 8) {
    s = s.slice(0, 8);
  }

  if (typeof max === "number" && Number.isFinite(max)) {
    if (s === "" || s === ".") return s;
    const n = Number(s);
    if (Number.isFinite(n) && n > max) {
      return String(max);
    }
  }

  return s;
};

const EMPTY_FORM = {
  acquirerTId: "",
  isActive: true,
  rcptHeader: "",
  rcptFooter: "",
  feeSetup: {
    feeType: 0,
    feePorD: 0,
    feeValue: "0",
    minPurchase: "0",
    maxPurchase: "29999.99",
    minFee: "0",
    maxFee: "0",
  },
};

// ---- Skeleton block rendered inside the modal body while loading ----
const TerminalFormSkeleton = () => (
  <div
    className="terminal-form-skeleton"
    aria-busy="true"
    aria-live="polite"
    aria-label="Loading terminal details"
  >
    {/* Row 1: Terminal ID + Enrolment Code */}
    <div className="row">
      <div className="col-md-6 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-input" />
        <div className="row mt-3">
          <div className="col-6">
            <div className="skeleton skeleton-toggle" />
          </div>
          <div className="col-6">
            <div className="skeleton skeleton-button" />
          </div>
        </div>
      </div>
      <div className="col-md-6 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="d-flex gap-2">
          <div className="skeleton skeleton-input flex-grow-1" />
          <div className="skeleton skeleton-button" style={{ width: 110 }} />
        </div>
        <div className="skeleton skeleton-info mt-2" />
      </div>
    </div>

    <hr />

    {/* Fee Setup */}
    <div className="skeleton skeleton-heading mb-3" />
    <div className="row">
      <div className="col-md-4 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-input" />
      </div>
      <div className="col-md-4 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-input" />
      </div>
      <div className="col-md-6 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-input" />
      </div>
      <div className="col-md-6 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-input" />
      </div>
      <div className="col-md-6 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-input" />
      </div>
      <div className="col-md-6 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-input" />
      </div>
    </div>

    <hr />

    {/* Receipt Setup */}
    <div className="skeleton skeleton-heading mb-3" />
    <div className="row">
      <div className="col-md-6 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-textarea" />
      </div>
      <div className="col-md-6 mb-3">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-textarea" />
      </div>
    </div>
  </div>
);

const BusinessTerminals = ({ businessId, onTerminalsUpdated, showToast }) => {
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTerminalForm, setShowTerminalForm] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingTerminal, setIsLoadingTerminal] = useState(false);
  const [isClearingLoginAttempts, setIsClearingLoginAttempts] = useState(false);
  const [isCreatingEnrolmentCode, setIsCreatingEnrolmentCode] = useState(false);
  const [enrolmentCode, setEnrolmentCode] = useState("");
  const [enrolmentExpiry, setEnrolmentExpiry] = useState(null);

  // 🔑 When false, the modal body renders the skeleton instead of the form.
  // Initialised to `true` so Add-mode (no fetch) never shows a loader.
  // Only `handleViewTerminal` flips this to `false` while it fetches.
  const [isFormReady, setIsFormReady] = useState(true);

  const [feeCaps, setFeeCaps] = useState({
    pFeeCap: DEFAULT_P_FEE_CAP,
    dFeeCap: DEFAULT_D_FEE_CAP,
  });

  const [formError, setFormError] = useState("");

  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedTerminalForLog, setSelectedTerminalForLog] = useState(null);

  const [, forceTick] = useState(0);

  const uid = useId();
  const fid = (name) => `${uid}-${name}`;

  const [sessionInfo, setSessionInfo] = useState({
    loginDate: null,
    lastPing: null,
    connectionType: null,
  });

  const [formData, setFormData] = useState(EMPTY_FORM);

  const isMountedRef = useRef(true);
  const timerRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const businessIdRef = useRef(businessId);

  // Atomic once-per-businessId guards (survive StrictMode remount)
  const terminalsStateRef = useRef({ loadedFor: null, loadingFor: null });
  const feeCapsStateRef = useRef({ loadedFor: null, loadingFor: null });

  const modalBodyRef = useRef(null);

  const showToastRef = useRef(showToast);
  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  // Toast wrapper — used ONLY for non-form messages (success, background)
  const toastSeqRef = useRef(0);
  const toast = useCallback((message, type = "info", sticky = false) => {
    toastSeqRef.current += 1;
    setTimeout(() => {
      const fn = showToastRef.current;
      if (typeof fn === "function") {
        try {
          fn(message, type, sticky);
          return;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[toast] parent showToast threw:", err);
        }
      }
      // eslint-disable-next-line no-console
      console.warn(`[toast:${type}] ${message}`);
    }, 0);
  }, []);

  useEffect(() => {
    if (businessId) businessIdRef.current = businessId;
  }, [businessId]);

  // ---- Session storage helpers ----
  const codeKey = (terminalId) => `${SESSION_KEY_PREFIX}${terminalId}`;
  const expiryKey = (terminalId) => `${EXPIRY_KEY_PREFIX}${terminalId}`;

  const saveEnrolmentToSession = (terminalId, code, expiry) => {
    if (!terminalId) return;
    sessionStorage.setItem(codeKey(terminalId), code);
    sessionStorage.setItem(expiryKey(terminalId), String(expiry));
  };

  const clearEnrolmentFromSession = (terminalId) => {
    if (!terminalId) return;
    sessionStorage.removeItem(codeKey(terminalId));
    sessionStorage.removeItem(expiryKey(terminalId));
  };

  const loadEnrolmentFromSession = (terminalId) => {
    if (!terminalId) return null;
    const code = sessionStorage.getItem(codeKey(terminalId));
    const expiryStr = sessionStorage.getItem(expiryKey(terminalId));
    if (!code || !expiryStr) return null;
    const expiry = parseInt(expiryStr, 10);
    if (Number.isNaN(expiry)) return null;
    return { code, expiry };
  };

  // ---- Central reset helper ----
  const resetTransientFlags = useCallback(() => {
    isSubmittingRef.current = false;
    setIsSaving(false);
    setIsLoadingTerminal(false);
  }, []);

  // ---- Show form error AND scroll modal body to top ----
  const showFormError = useCallback((message) => {
    setFormError(message);
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    setTimeout(() => {
      const alertEl = document.querySelector(".modal .alert-danger");
      if (alertEl && typeof alertEl.scrollIntoView === "function") {
        alertEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 60);
  }, []);

  // ---- Cleanup on unmount ----
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // ---- Fetch terminals (guarded so it fires ONCE per businessId) ----
  const fetchTerminals = useCallback(
    async (force = false) => {
      if (!businessId) return;

      const state = terminalsStateRef.current;

      if (!force && state.loadedFor === businessId) return;
      if (state.loadingFor === businessId) return;

      state.loadingFor = businessId;
      setLoading(true);

      try {
        const data = await getTerminals(businessId);
        if (isMountedRef.current) {
          const list = Array.isArray(data) ? data : data?.data || [];
          setTerminals(list);
          if (onTerminalsUpdated) onTerminalsUpdated(null, list.length);
          state.loadedFor = businessId;
        }
      } catch (err) {
        if (isMountedRef.current) toast("Failed to load terminals", "error");
      } finally {
        if (isMountedRef.current) setLoading(false);
        if (state.loadingFor === businessId) {
          state.loadingFor = null;
        }
      }
    },
    [businessId, toast, onTerminalsUpdated],
  );

  // Fetch terminals ONCE per businessId (StrictMode-safe)
  useEffect(() => {
    fetchTerminals();
  }, [fetchTerminals]);

  // ---- Fetch fee caps ONCE per businessId (StrictMode-safe) ----
  useEffect(() => {
    if (!businessId) return;

    const state = feeCapsStateRef.current;

    if (state.loadedFor === businessId) return;
    if (state.loadingFor === businessId) return;

    state.loadingFor = businessId;

    (async () => {
      try {
        const data = await getFeeCaps(businessId);
        if (!isMountedRef.current) return;
        if (data) {
          setFeeCaps({
            pFeeCap: data.pFeeCap,
            dFeeCap: data.dFeeCap,
          });
        }
        state.loadedFor = businessId;
      } catch (err) {
        if (isMountedRef.current) toast("Failed to load fee caps", "error");
      } finally {
        if (state.loadingFor === businessId) {
          state.loadingFor = null;
        }
      }
    })();
  }, [businessId, toast]);

  // ---- Defensive: reset transient flags + clear inline error on open ----
  useEffect(() => {
    if (showTerminalForm) {
      resetTransientFlags();
      setFormError("");
    }
  }, [showTerminalForm, resetTransientFlags]);

  // ---- Countdown timer ----
  const startEnrolmentTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setInterval(() => {
      if (!isMountedRef.current) return;
      forceTick((n) => n + 1);

      setEnrolmentExpiry((currentExpiry) => {
        if (currentExpiry && Date.now() >= currentExpiry) {
          if (editingTerminal?.acquirerTId) {
            clearEnrolmentFromSession(editingTerminal.acquirerTId);
          }
          setEnrolmentCode("");
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          toast("Enrolment code has expired", "warning");
          return null;
        }
        return currentExpiry;
      });
    }, 1000);
  }, [editingTerminal, toast]);

  // ---- Load saved enrolment code ----
  useEffect(() => {
    if (!showTerminalForm || !isFormReady || !editingTerminal?.acquirerTId)
      return;

    const saved = loadEnrolmentFromSession(editingTerminal.acquirerTId);
    if (saved && Date.now() < saved.expiry) {
      setEnrolmentCode(saved.code);
      setEnrolmentExpiry(saved.expiry);
      startEnrolmentTimer();
    } else {
      clearEnrolmentFromSession(editingTerminal.acquirerTId);
      setEnrolmentCode("");
      setEnrolmentExpiry(null);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [showTerminalForm, isFormReady, editingTerminal, startEnrolmentTimer]);

  // ---- Load a terminal into formData ----
  const populateFormFromTerminal = useCallback((terminal) => {
    if (!terminal) return;

    isSubmittingRef.current = false;
    setIsSaving(false);
    setFormError("");

    setFormData({
      acquirerTId: terminal.acquirerTId || "",
      isActive: terminal.isActive !== undefined ? terminal.isActive : true,
      rcptHeader: terminal.rcptHeader || "",
      rcptFooter: terminal.rcptFooter || "",
      feeSetup: {
        feeType: terminal.feeSetup?.feeType ?? 0,
        feePorD: terminal.feeSetup?.feePorD ?? 0,
        feeValue: String(terminal.feeSetup?.feeValue ?? 0),
        minPurchase: String(terminal.feeSetup?.minPurchase ?? 0),
        maxPurchase: String(terminal.feeSetup?.maxPurchase ?? 29999.99),
        minFee: String(terminal.feeSetup?.minFee ?? 0),
        maxFee: String(terminal.feeSetup?.maxFee ?? 0),
      },
    });

    const loginProps = Array.isArray(terminal.loginProps)
      ? terminal.loginProps
      : [];

    const latest =
      loginProps.length > 0
        ? loginProps.reduce((best, curr) => {
            const bestTime = Math.max(
              best.loginDate ? new Date(best.loginDate).getTime() : 0,
              best.lastPing ? new Date(best.lastPing).getTime() : 0,
            );
            const currTime = Math.max(
              curr.loginDate ? new Date(curr.loginDate).getTime() : 0,
              curr.lastPing ? new Date(curr.lastPing).getTime() : 0,
            );
            return currTime > bestTime ? curr : best;
          })
        : null;

    setSessionInfo({
      loginDate: latest?.loginDate ?? null,
      lastPing: latest?.lastPing ?? null,
      connectionType: latest?.connectionType ?? null,
    });
  }, []);

  // ---- Open a terminal: skeleton until fresh data arrives ----
  const handleViewTerminal = useCallback(
    async (terminal) => {
      resetTransientFlags();
      setFormError("");
      setEditingTerminal(null);
      setIsViewMode(true);
      setIsLoadingTerminal(true);
      setIsFormReady(false); // 🔑 keep skeleton visible
      setShowTerminalForm(true);
      setFormData(EMPTY_FORM);
      setSessionInfo({
        loginDate: null,
        lastPing: null,
        connectionType: null,
      });
      setEnrolmentCode("");
      setEnrolmentExpiry(null);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      try {
        const response = await getSingleTerminal(terminal.id);
        const freshTerminal = response?.data || response;
        if (!isMountedRef.current) return;

        if (freshTerminal) {
          setEditingTerminal(freshTerminal);
          populateFormFromTerminal(freshTerminal);
          setIsFormReady(true); // 🔑 only now do we render the form
        } else {
          // API returned nothing — fall back so the user still sees something
          setEditingTerminal(terminal);
          populateFormFromTerminal(terminal);
          setIsFormReady(true);
        }
      } catch (err) {
        if (isMountedRef.current) {
          showFormError(
            err.response?.data?.message ||
              err.message ||
              "Failed to load terminal details",
          );
          setEditingTerminal(terminal);
          populateFormFromTerminal(terminal);
          setIsFormReady(true);
        }
      } finally {
        if (isMountedRef.current) setIsLoadingTerminal(false);
      }
    },
    [resetTransientFlags, populateFormFromTerminal, showFormError],
  );

  // ---- Add new terminal (no fetch → form is immediately ready) ----
  const handleAddTerminal = useCallback(() => {
    resetTransientFlags();
    setFormError("");
    setEditingTerminal(null);
    setIsViewMode(false);
    setIsFormReady(true); // 🔑 no fetch — never show a loader
    setEnrolmentCode("");
    setEnrolmentExpiry(null);
    setSessionInfo({ loginDate: null, lastPing: null, connectionType: null });
    setFormData(EMPTY_FORM);
    setShowTerminalForm(true);
  }, [resetTransientFlags]);

  // ---- Switch view → edit ----
  const handleEditTerminal = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      resetTransientFlags();
      setFormError("");
      setIsViewMode(false);
      setIsFormReady(true);
    },
    [resetTransientFlags],
  );

  // ---- Close modal ----
  const closeForm = useCallback(() => {
    setShowTerminalForm(false);
    setIsViewMode(false);
    setEditingTerminal(null);
    setFormError("");
    resetTransientFlags();
    setIsFormReady(true); // 🔑 reset so the next Add never shows a loader
    setEnrolmentCode("");
    setEnrolmentExpiry(null);
    setSessionInfo({ loginDate: null, lastPing: null, connectionType: null });
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [resetTransientFlags]);

  // ---- Clear login attempts ----
  const handleClearLoginAttempts = async () => {
    if (!editingTerminal?.acquirerTId) {
      showFormError("No terminal selected");
      return;
    }
    setIsClearingLoginAttempts(true);
    setFormError("");
    try {
      const response = await clearTerminalLoginAttempts(
        editingTerminal.acquirerTId,
      );
      toast(
        response?.message || "Login attempts cleared successfully",
        "success",
      );
    } catch (err) {
      showFormError(
        err.response?.data?.message ||
          err.message ||
          "Failed to clear login attempts",
      );
    } finally {
      setIsClearingLoginAttempts(false);
    }
  };

  // ---- Create enrolment code ----
  const handleCreateEnrolmentCode = async () => {
    if (!editingTerminal?.id) {
      showFormError("No terminal selected");
      return;
    }
    setIsCreatingEnrolmentCode(true);
    setFormError("");
    try {
      const response = await createEnrolmentCode(editingTerminal.id);
      const code = response?.code || "";
      if (!code) throw new Error("No enrolment code returned from server");

      const expiryTime = Date.now() + ENROLMENT_DURATION_MS;
      saveEnrolmentToSession(editingTerminal.acquirerTId, code, expiryTime);

      setEnrolmentCode(code);
      setEnrolmentExpiry(expiryTime);
      startEnrolmentTimer();

      toast(
        `Enrolment code generated! Expires at ${formatExpiryTime(expiryTime)}`,
        "success",
      );
    } catch (err) {
      showFormError(
        err.response?.data?.message ||
          err.message ||
          "Failed to create enrolment code",
      );
    } finally {
      setIsCreatingEnrolmentCode(false);
    }
  };

  const formatExpiryTime = (expiryTime) => {
    const date = new Date(expiryTime);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getRemainingTime = (expiryTime) => {
    if (!expiryTime) return "";
    const remaining = Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    let str = String(value);
    const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(str);
    if (!hasTimezone) str = `${str}Z`;
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-NZ", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const getConnectionIcon = (value) => {
    const map = {
      WIFI: "bi-wifi",
      ETHERNET: "bi-ethernet",
      MOBILE_DATA: "bi-phone",
      NONE: "bi-question-circle",
      OTHER: "bi-three-dots",
    };
    return map[value] ?? "bi-question-circle";
  };

  // ---- Resolve per-field max cap ----
  const getFieldMax = useCallback(
    (field) => {
      switch (field) {
        case "feeValue":
          return formData.feeSetup.feePorD === 0
            ? feeCaps.pFeeCap
            : feeCaps.dFeeCap;
        case "minPurchase":
          return MAX_PURCHASE;
        case "maxPurchase":
          return MAX_PURCHASE;
        case "minFee":
          return MAX_FEE;
        case "maxFee":
          return MAX_FEE;
        default:
          return undefined;
      }
    },
    [formData.feeSetup.feePorD, feeCaps.pFeeCap, feeCaps.dFeeCap],
  );

  // ---- Fee field update helper ----
  const updateFeeField = (field, rawValue) => {
    const cap = getFieldMax(field);
    const cleaned = sanitizeDecimal(rawValue, cap);
    setFormData((prev) => ({
      ...prev,
      feeSetup: { ...prev.feeSetup, [field]: cleaned },
    }));
    if (formError) setFormError("");
  };

  // ---- Validation ----
  const validateFeeSetup = () => {
    const { feeType, feeValue, minPurchase, maxPurchase, minFee, maxFee } =
      formData.feeSetup;

    if (Number(feeType) === 0) return null;

    const fv = toNum(feeValue, 0);
    const minP = toNum(minPurchase, 0);
    const maxP = toNum(maxPurchase, 29999.99);
    const minF = toNum(minFee, 0);
    const maxF = toNum(maxFee, 0);

    if (fv < 0) return "Fee value cannot be negative";
    if (minP < 0) return "Min Purchase cannot be negative";
    if (maxP < 0) return "Max Purchase cannot be negative";
    if (minF < 0) return "Min Fee cannot be negative";
    if (maxF < 0) return "Max Fee cannot be negative";

    if (minP > maxP) return "Min Purchase cannot be greater than Max Purchase";
    if (minF > maxF) return "Min Fee cannot be greater than Max Fee";

    if (maxP > MAX_PURCHASE)
      return `Max Purchase cannot exceed ${MAX_PURCHASE}`;
    if (maxF > MAX_FEE) return `Max Fee cannot exceed ${MAX_FEE}`;

    if (Number(formData.feeSetup.feePorD) === 0) {
      if (fv < 0 || fv > feeCaps.pFeeCap) {
        return `Percentage fee must be between 0 and ${feeCaps.pFeeCap}`;
      }
    } else {
      if (fv < 0 || fv > feeCaps.dFeeCap) {
        return `Fixed fee must be between 0 and ${feeCaps.dFeeCap}`;
      }
    }

    return null;
  };

  // ---- Submit (add or update) ----
  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();

      if (isSubmittingRef.current || isSaving) {
        showFormError("Please wait — a save is already in progress");
        return;
      }

      setFormError("");

      const currentBusinessId = businessIdRef.current;

      const trimmedId = (formData.acquirerTId || "").trim();
      if (!trimmedId) {
        showFormError("Terminal ID is required");
        return;
      }
      if (trimmedId.length < 5 || trimmedId.length > 25) {
        showFormError("Terminal ID must be 5–25 characters");
        return;
      }
      if (!/^[A-Z0-9]+$/.test(trimmedId)) {
        showFormError(
          "Terminal ID can only contain uppercase letters and numbers",
        );
        return;
      }

      if (!currentBusinessId) {
        showFormError("Business ID is missing");
        return;
      }

      const feeError = validateFeeSetup();
      if (feeError) {
        showFormError(feeError);
        return;
      }

      isSubmittingRef.current = true;
      setIsSaving(true);

      let succeeded = false;
      const isUpdate = !!editingTerminal;

      try {
        const terminalData = {
          acquirerTId: trimmedId,
          rcptHeader: formData.rcptHeader || "",
          rcptFooter: formData.rcptFooter || "",
          isActive: editingTerminal ? formData.isActive : true,
          feeSetup: {
            feeType: toNum(formData.feeSetup.feeType, 0),
            feePorD: toNum(formData.feeSetup.feePorD, 0),
            feeValue: toNum(formData.feeSetup.feeValue, 0),
            minPurchase: toNum(formData.feeSetup.minPurchase, 0),
            maxPurchase: toNum(formData.feeSetup.maxPurchase, 29999.99),
            minFee: toNum(formData.feeSetup.minFee, 0),
            maxFee: toNum(formData.feeSetup.maxFee, 0),
          },
        };
        if (editingTerminal) {
          terminalData.id = editingTerminal.id;
          terminalData.version = editingTerminal.version || 0;
        } else {
          terminalData.version = 0;
        }

        const response = await addOrUpdateTerminal(
          currentBusinessId,
          terminalData,
        );
        const updatedBusinessData = response.data || response;
        if (onTerminalsUpdated) onTerminalsUpdated(updatedBusinessData);

        await fetchTerminals(true);

        toast(
          isUpdate
            ? "Terminal updated successfully"
            : "Terminal added successfully",
          "success",
        );
        succeeded = true;
      } catch (err) {
        if (err.statusCode === 409 || err.response?.status === 409) {
          const conflictMessage =
            err.details ||
            err.response?.data?.detail ||
            err.response?.data?.message ||
            "Conflict detected. Please refresh and try again.";
          showFormError(conflictMessage);

          const terminalId = editingTerminal?.id;
          if (terminalId) {
            try {
              const response = await getSingleTerminal(terminalId);
              const freshTerminal = response?.data || response;
              if (isMountedRef.current && freshTerminal) {
                setEditingTerminal(freshTerminal);
                populateFormFromTerminal(freshTerminal);
                setIsViewMode(true);
              }
            } catch (fetchErr) {
              if (fetchErr?.statusCode === 404) {
                if (isMountedRef.current) {
                  showFormError("This terminal no longer exists");
                  setShowTerminalForm(false);
                  setIsViewMode(false);
                  setEditingTerminal(null);
                }
                fetchTerminals(true);
              } else if (isMountedRef.current) {
                showFormError("Failed to refresh terminal data");
              }
            }
          }
        } else {
          const action = isUpdate ? "update" : "add";
          const errMsg =
            err.response?.data?.message ||
            err.response?.data?.detail ||
            err.message ||
            `Failed to ${action} terminal`;
          showFormError(`Failed to ${action} terminal: ${errMsg}`);
        }
      } finally {
        isSubmittingRef.current = false;
        setIsSaving(false);
        if (succeeded) closeForm();
      }
    },
    [
      isSaving,
      formData,
      editingTerminal,
      toast,
      onTerminalsUpdated,
      fetchTerminals,
      closeForm,
      populateFormFromTerminal,
      showFormError,
    ],
  );

  // ---- Table columns ----
  const columns = [
    {
      name: "Terminal ID",
      selector: (row) => row.acquirerTId,
      sortable: true,
      cell: (row) => (
        <span
          className="business-name-link"
          onClick={() => handleViewTerminal(row)}
          style={{ cursor: "pointer", color: "var(--color-blaze-orange)" }}
        >
          {row.acquirerTId || "N/A"}
        </span>
      ),
    },
    {
      name: "Status",
      selector: (row) => (row.isActive ? "Active" : "Inactive"),
      sortable: true,
      cell: (row) => (
        <span className={`badge ${row.isActive ? "bg-success" : "bg-danger"}`}>
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Fee Type",
      selector: (row) => {
        const feeTypes = ["None", "Service Fee", "Admin Fee", "Software Fee"];
        return feeTypes[row.feeSetup?.feeType] || "N/A";
      },
      sortable: true,
    },
    {
      name: "Fee Value",
      selector: (row) => {
        if (row.feeSetup?.feeType === 0) return "-";
        const type = row.feeSetup?.feePorD === 0 ? "Percentage" : "Fixed";
        if (type === "Percentage") return `${row.feeSetup?.feeValue || 0}%`;
        if (type === "Fixed") return `$${row.feeSetup?.feeValue || 0}`;
        return row.feeSetup?.feeValue || 0;
      },
      sortable: true,
    },
    {
      name: "Action",
      sortable: false,
      cell: (row) => (
        <PermissionGate permission={PERMISSIONS.TERMINAL_GET_LOG}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              setSelectedTerminalForLog(row);
              setShowLogModal(true);
            }}
            title="View Activity Log"
          >
            <i className="bi bi-clock-history"></i>
          </button>
        </PermissionGate>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const feeTypeOptions = [
    { value: 0, label: "None" },
    { value: 1, label: "Service Fee" },
    { value: 2, label: "Admin Fee" },
    { value: 3, label: "Software Fee" },
  ];

  const isCodeValid =
    enrolmentCode && enrolmentExpiry && Date.now() < enrolmentExpiry;

  const hasSessionInfo =
    sessionInfo.loginDate || sessionInfo.lastPing || sessionInfo.connectionType;

  const numericKeyDown = (e) => {
    const allowed =
      [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Home",
        "End",
        "Enter",
      ].includes(e.key) ||
      e.ctrlKey ||
      e.metaKey;
    if (allowed) return;
    if (!/[0-9.]/.test(e.key)) {
      e.preventDefault();
    }
  };

  // 🔑 Show skeleton only while the single-terminal fetch is in flight.
  // Never show it for Add mode (isFormReady starts true and stays true).
  const showModalSkeleton = isLoadingTerminal || !isFormReady;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          <i className="bi bi-terminal me-2"></i>
          Terminals ({terminals.length})
        </h6>
        <PermissionGate permission={PERMISSIONS.TERMINAL_UPSERT}>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleAddTerminal}
          >
            <i className="bi bi-plus-circle me-1"></i>Add Terminal
          </button>
        </PermissionGate>
      </div>

      <DataTable
        columns={columns}
        data={terminals}
        progressPending={loading}
        progressComponent={
          <Loader message="Loading terminals..." size="medium" />
        }
        pagination
        paginationPerPage={5}
        highlightOnHover
        striped
        className="business-table"
        noDataComponent={
          <div className="text-center py-4">
            <i
              className="bi bi-terminal display-4"
              style={{ color: "var(--color-peach-glow)" }}
            ></i>
            <p className="text-theme-muted mt-2">No terminals found</p>
          </div>
        }
      />

      {showTerminalForm && (
        <div className="modal d-block" style={{ zIndex: 1051 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-terminal me-2"></i>
                  {isViewMode
                    ? "Terminal Details"
                    : editingTerminal
                      ? "Edit Terminal"
                      : "Add Terminal"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeForm}
                  disabled={isSaving}
                />
              </div>

              {formError && !showModalSkeleton && (
                <div
                  className="alert alert-danger d-flex align-items-start gap-2 mb-0 rounded-0 border-start-0 border-end-0"
                  role="alert"
                  style={{ fontSize: "0.9rem" }}
                >
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <div className="flex-grow-1 fw-semibold">{formError}</div>
                  <button
                    type="button"
                    className="btn-close btn-close-sm"
                    aria-label="Close"
                    onClick={() => setFormError("")}
                  />
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="modal-body" ref={modalBodyRef}>
                  {showModalSkeleton ? (
                    // 🔑 Skeleton loader — no text. Rendered while the
                    // single-terminal fetch is in flight.
                    <TerminalFormSkeleton />
                  ) : (
                    <>
                      <div className="row">
                        <div className="col-md-6 mb-3 mb-md-0">
                          <label
                            className="form-label"
                            htmlFor={fid("acquirerTId")}
                          >
                            Terminal ID
                          </label>
                          <input
                            id={fid("acquirerTId")}
                            name="acquirerTId"
                            type="text"
                            className="form-control"
                            value={formData.acquirerTId}
                            onChange={(e) => {
                              const cleaned = e.target.value
                                .replace(/[^a-zA-Z0-9]/g, "")
                                .toUpperCase();
                              setFormData((prev) => ({
                                ...prev,
                                acquirerTId: cleaned,
                              }));
                              if (formError) setFormError("");
                            }}
                            onKeyDown={(e) => {
                              const allowed =
                                /[a-zA-Z0-9]/.test(e.key) ||
                                [
                                  "Backspace",
                                  "Delete",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Tab",
                                  "Home",
                                  "End",
                                ].includes(e.key) ||
                                e.ctrlKey ||
                                e.metaKey;
                              if (!allowed) e.preventDefault();
                            }}
                            placeholder="Enter terminal ID"
                            minLength={5}
                            maxLength={25}
                            disabled={isViewMode}
                            required
                            pattern="[A-Z0-9]+"
                            title="Only uppercase letters and numbers are allowed (5–25 characters)"
                          />
                          {isViewMode || editingTerminal ? (
                            <div className="row">
                              <div className="col-6 mt-3">
                                <LabeledToggle
                                  id={fid("isActive")}
                                  checked={formData.isActive}
                                  disabled={isViewMode}
                                  onChange={(checked) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      isActive: checked,
                                    }));
                                    if (formError) setFormError("");
                                  }}
                                />
                              </div>
                              <div className="col-6 mt-3">
                                <PermissionGate
                                  permission={PERMISSIONS.TERMINAL_UPSERT}
                                >
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={handleClearLoginAttempts}
                                    disabled={
                                      isClearingLoginAttempts ||
                                      !editingTerminal
                                    }
                                  >
                                    {isClearingLoginAttempts ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                        Clearing...
                                      </>
                                    ) : (
                                      "Clear Login Attempts"
                                    )}
                                  </button>
                                </PermissionGate>{" "}
                              </div>
                            </div>
                          ) : null}
                        </div>

                        {isViewMode || editingTerminal ? (
                          <div className="col-md-6 mb-3 mb-md-0">
                            <label
                              className="form-label"
                              htmlFor={fid("enrolmentcode")}
                            >
                              Enrolment Code
                            </label>
                            <div className="d-flex flex-row align-items-center gap-2">
                              <input
                                id={fid("enrolmentcode")}
                                type="text"
                                className="form-control flex-grow-1"
                                name="enrolmentcode"
                                placeholder="Click button for code"
                                value={enrolmentCode}
                                readOnly
                              />
                              <PermissionGate
                                permission={PERMISSIONS.TERMINAL_ENROL_CODE}
                              >
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm text-nowrap flex-shrink-0"
                                  onClick={handleCreateEnrolmentCode}
                                  disabled={isCreatingEnrolmentCode}
                                  style={{ minWidth: "110px" }}
                                >
                                  {isCreatingEnrolmentCode ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" />
                                      Generating...
                                    </>
                                  ) : enrolmentCode ? (
                                    "Regenerate"
                                  ) : (
                                    "Generate"
                                  )}
                                </button>
                              </PermissionGate>
                            </div>

                            {isCodeValid && (
                              <div className="mt-1">
                                <small className="text-success">
                                  Expires at {formatExpiryTime(enrolmentExpiry)}
                                  <span className="ms-2 badge bg-danger">
                                    {getRemainingTime(enrolmentExpiry)}
                                  </span>
                                </small>
                              </div>
                            )}
                            {enrolmentCode &&
                              enrolmentExpiry &&
                              Date.now() >= enrolmentExpiry && (
                                <div className="mt-1">
                                  <small className="text-danger">
                                    Code expired
                                  </small>
                                </div>
                              )}

                            {(isViewMode || editingTerminal) && (
                              <div className="col-8 mt-2 info-div d-flex flex-row align-items-center gap-1">
                                {hasSessionInfo ? (
                                  <>
                                    <div className="flex-fill">
                                      {sessionInfo.loginDate && (
                                        <div className="d-flex align-items-baseline gap-2">
                                          <span className="fw-semibold small">
                                            Last Login
                                          </span>
                                          <small className="text-theme-muted">
                                            {formatDateTime(
                                              sessionInfo.loginDate,
                                            )}
                                          </small>
                                        </div>
                                      )}
                                      {sessionInfo.lastPing && (
                                        <div className="d-flex align-items-baseline gap-2 mt-1">
                                          <span className="fw-semibold small">
                                            Last Ping
                                          </span>
                                          <small className="text-theme-muted">
                                            {formatDateTime(
                                              sessionInfo.lastPing,
                                            )}
                                          </small>
                                        </div>
                                      )}
                                    </div>

                                    {sessionInfo.connectionType &&
                                      sessionInfo.connectionType !== "" && (
                                        <div className="d-flex flex-column align-items-center text-center">
                                          <span className="text-blaze-orange">
                                            <i
                                              className={`bi ${getConnectionIcon(
                                                sessionInfo.connectionType,
                                              )} fs-5`}
                                            ></i>
                                          </span>
                                        </div>
                                      )}
                                  </>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-plug text-theme-muted fs-5"></i>
                                    <small className="text-theme-muted">
                                      Not logged in
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                      <hr />
                      <h5 className="text-blaze-orange mb-3">Fee Setup</h5>

                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label
                            className="form-label"
                            htmlFor={fid("feeType")}
                          >
                            Fee Type
                          </label>
                          <Select
                            inputId={fid("feeType")}
                            options={feeTypeOptions}
                            value={
                              formData.feeSetup?.feeType !== undefined &&
                              formData.feeSetup?.feeType !== null
                                ? feeTypeOptions.find(
                                    (option) =>
                                      option.value ===
                                      formData.feeSetup.feeType,
                                  )
                                : null
                            }
                            onChange={(selected) => {
                              setFormData((prev) => ({
                                ...prev,
                                feeSetup: {
                                  ...prev.feeSetup,
                                  feeType: selected ? selected.value : 0,
                                },
                              }));
                              if (formError) setFormError("");
                            }}
                            isDisabled={isViewMode}
                            placeholder="Select Fee Type"
                            isClearable
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label
                            className="form-label"
                            htmlFor={fid("feeValue")}
                          >
                            Fee Value{" "}
                            {formData.feeSetup.feePorD === 0 ? "(%)" : "($)"}
                          </label>
                          <div className="d-flex align-items-center gap-2">
                            <div className="radio-vertical">
                              <label
                                className="custom-radio justify-content-end"
                                htmlFor={fid("feePorD-percent")}
                              >
                                <span className="radio-label">%</span>
                                <input
                                  id={fid("feePorD-percent")}
                                  type="radio"
                                  name={fid("feeTypeValue")}
                                  value="percentage"
                                  checked={formData.feeSetup.feePorD === 0}
                                  onChange={() => {
                                    setFormData((prev) => {
                                      const capped = sanitizeDecimal(
                                        prev.feeSetup.feeValue,
                                        feeCaps.pFeeCap,
                                      );
                                      return {
                                        ...prev,
                                        feeSetup: {
                                          ...prev.feeSetup,
                                          feePorD: 0,
                                          feeValue: capped,
                                        },
                                      };
                                    });
                                    if (formError) setFormError("");
                                  }}
                                  disabled={
                                    isViewMode ||
                                    formData.feeSetup.feeType === 0
                                  }
                                />
                                <span className="radio-mark" />
                              </label>
                              <label
                                className="custom-radio justify-content-end"
                                htmlFor={fid("feePorD-fixed")}
                              >
                                <span className="radio-label">$</span>
                                <input
                                  id={fid("feePorD-fixed")}
                                  type="radio"
                                  name={fid("feeTypeValue")}
                                  value="fixed"
                                  checked={formData.feeSetup.feePorD === 1}
                                  onChange={() => {
                                    setFormData((prev) => {
                                      const capped = sanitizeDecimal(
                                        prev.feeSetup.feeValue,
                                        feeCaps.dFeeCap,
                                      );
                                      return {
                                        ...prev,
                                        feeSetup: {
                                          ...prev.feeSetup,
                                          feePorD: 1,
                                          feeValue: capped,
                                        },
                                      };
                                    });
                                    if (formError) setFormError("");
                                  }}
                                  disabled={
                                    isViewMode ||
                                    formData.feeSetup.feeType === 0
                                  }
                                />
                                <span className="radio-mark" />
                              </label>
                            </div>
                            <input
                              id={fid("feeValue")}
                              name="feeValue"
                              type="text"
                              inputMode="decimal"
                              className="form-control"
                              value={formData.feeSetup.feeValue}
                              onChange={(e) =>
                                updateFeeField("feeValue", e.target.value)
                              }
                              onKeyDown={numericKeyDown}
                              placeholder="0.00"
                              disabled={
                                isViewMode || formData.feeSetup.feeType === 0
                              }
                              title={
                                formData.feeSetup.feePorD === 0
                                  ? `Max ${feeCaps.pFeeCap}`
                                  : `Max ${feeCaps.dFeeCap}`
                              }
                            />
                          </div>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            className="form-label"
                            htmlFor={fid("minPurchase")}
                          >
                            Min Purchase
                          </label>
                          <input
                            id={fid("minPurchase")}
                            name="minPurchase"
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={formData.feeSetup.minPurchase}
                            onChange={(e) =>
                              updateFeeField("minPurchase", e.target.value)
                            }
                            onKeyDown={numericKeyDown}
                            placeholder="0.00"
                            disabled={
                              isViewMode || formData.feeSetup.feeType === 0
                            }
                            title="Max 29999.99"
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            className="form-label"
                            htmlFor={fid("maxPurchase")}
                          >
                            Max Purchase
                          </label>
                          <input
                            id={fid("maxPurchase")}
                            name="maxPurchase"
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={formData.feeSetup.maxPurchase}
                            onChange={(e) =>
                              updateFeeField("maxPurchase", e.target.value)
                            }
                            onKeyDown={numericKeyDown}
                            placeholder="29999.99"
                            disabled={
                              isViewMode || formData.feeSetup.feeType === 0
                            }
                            title="Max 29999.99"
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label" htmlFor={fid("minFee")}>
                            Min Fee
                          </label>
                          <input
                            id={fid("minFee")}
                            name="minFee"
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={formData.feeSetup.minFee}
                            onChange={(e) =>
                              updateFeeField("minFee", e.target.value)
                            }
                            onKeyDown={numericKeyDown}
                            placeholder="0.00"
                            disabled={
                              isViewMode || formData.feeSetup.feeType === 0
                            }
                            title="Max 99999.99"
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label" htmlFor={fid("maxFee")}>
                            Max Fee
                          </label>
                          <input
                            id={fid("maxFee")}
                            name="maxFee"
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={formData.feeSetup.maxFee}
                            onChange={(e) =>
                              updateFeeField("maxFee", e.target.value)
                            }
                            onKeyDown={numericKeyDown}
                            placeholder="0.00"
                            disabled={
                              isViewMode || formData.feeSetup.feeType === 0
                            }
                            title="Max 99999.99"
                          />
                        </div>
                      </div>

                      <hr />
                      <h5 className="text-blaze-orange mb-3">Receipt Setup</h5>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label
                            className="form-label"
                            htmlFor={fid("rcptHeader")}
                          >
                            Receipt Header
                          </label>
                          <textarea
                            id={fid("rcptHeader")}
                            name="rcptHeader"
                            className="form-control"
                            value={formData.rcptHeader}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                rcptHeader: e.target.value,
                              }));
                              if (formError) setFormError("");
                            }}
                            placeholder="Enter receipt header"
                            disabled={isViewMode}
                            rows="5"
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            className="form-label"
                            htmlFor={fid("rcptFooter")}
                          >
                            Receipt Footer
                          </label>
                          <textarea
                            id={fid("rcptFooter")}
                            name="rcptFooter"
                            className="form-control"
                            value={formData.rcptFooter}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                rcptFooter: e.target.value,
                              }));
                              if (formError) setFormError("");
                            }}
                            placeholder="Enter receipt footer"
                            disabled={isViewMode}
                            rows="5"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  {isViewMode ? (
                    <PermissionGate permission={PERMISSIONS.TERMINAL_UPSERT}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleEditTerminal}
                        disabled={isSaving || showModalSkeleton}
                      >
                        <i className="bi bi-pencil me-1"></i>Edit Terminal
                      </button>
                    </PermissionGate>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeForm}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSaving || showModalSkeleton}
                      >
                        {isSaving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Saving...
                          </>
                        ) : editingTerminal ? (
                          "Update Terminal"
                        ) : (
                          "Add Terminal"
                        )}
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <PermissionGate permission={PERMISSIONS.TERMINAL_GET_LOG}>
        <ActivityLogModal
          show={showLogModal}
          onClose={() => {
            setShowLogModal(false);
            setSelectedTerminalForLog(null);
          }}
          entityType="Terminal"
          entityId={selectedTerminalForLog?.id}
          title={`Activity Log — ${selectedTerminalForLog?.acquirerTId || "Terminal"}`}
          filterTypes={TERMINAL_FILTER_TYPES}
        />
      </PermissionGate>
    </div>
  );
};

export default BusinessTerminals;
